// ═══════════════════════════════════════════════════════════════════════════════
// Subscription upgrade card copy (§22.9) — Admin-editable, open locale map
// ═══════════════════════════════════════════════════════════════════════════════
import { LANGUAGE } from '../constants/index.js';
import { SUBSCRIPTION_BILLING_INTERVAL } from './subscriptionPromotions.js';
export const SUBSCRIPTION_UPGRADE_COPY_SETTING_KEY = 'subscription_upgrade_copy';
export const SUBSCRIPTION_UPGRADE_COPY_MAX_TITLE_LENGTH = 300;
export const SUBSCRIPTION_UPGRADE_COPY_MAX_LINE_LENGTH = 400;
export const SUBSCRIPTION_UPGRADE_COPY_MAX_LOCALES = 50;
export const SUBSCRIPTION_UPGRADE_COPY_AMOUNT_PLACEHOLDER = '{{amount}}';
export const SUBSCRIPTION_UPGRADE_COPY_DUE_AMOUNT_PLACEHOLDER = '{{dueAmount}}';
export const SUBSCRIPTION_UPGRADE_COPY_LIST_PRICE_PLACEHOLDER = '{{listPrice}}';
export const SUBSCRIPTION_UPGRADE_COPY_ENDS_AT_PLACEHOLDER = '{{endsAt}}';
export const SUBSCRIPTION_UPGRADE_COPY_DATE_PLACEHOLDER = '{{date}}';
export const SUBSCRIPTION_UPGRADE_COPY_START_PLACEHOLDER = '{{start}}';
export const SUBSCRIPTION_UPGRADE_COPY_END_PLACEHOLDER = '{{end}}';
/** Filled at runtime: 每月/每年, per month/per year, etc. */
export const SUBSCRIPTION_UPGRADE_COPY_PERIOD_PLACEHOLDER = '{{period}}';
/** Locale codes are open-ended (not limited to app LANGUAGE list). */
export const SUBSCRIPTION_UPGRADE_COPY_LOCALE_CODE_REGEX = /^[A-Za-z]{2,8}(-[A-Za-z0-9]{1,8}){0,3}$/;
const LOCALE_ZH_HANT = {
    payTodayTitle: '立即成為高級會員，以解鎖更多功能',
    payTodayAmount: '{{period}}只需{{amount}}',
    promoFirstMonthPrice: '首月只需 {{dueAmount}}，其後{{period}} {{listPrice}}',
    promoLimitedTimePrice: '活動期間{{period}}只需 {{dueAmount}}（直至 {{endsAt}}），其後{{period}} {{listPrice}}',
    promoFirstMonthBadge: '首月優惠：減免 {{amount}}',
    promoLimitedTimeBadge: '期間限定優惠：減免 {{amount}}（直至 {{endsAt}}）',
    deferredTitle: '設定訂閱（高級會員試用期內先不扣款）',
    deferredNoChargeToday: '今日無需付款',
    deferredFirstChargeOn: '首次扣款將於 {{date}} 進行',
    deferredCoversPeriod: '首個付費期：{{start}} 至 {{end}}',
    deferredInvitationTrialNote: '邀請碼試用至 {{date}}，系統會於試用結束當日開始首次扣款。',
};
const LOCALE_ZH_HANS = {
    payTodayTitle: '立即成为高级会员，以解锁更多功能',
    payTodayAmount: '{{period}}只需{{amount}}',
    promoFirstMonthPrice: '首月只需 {{dueAmount}}，其后{{period}} {{listPrice}}',
    promoLimitedTimePrice: '活动期间{{period}}只需 {{dueAmount}}（直至 {{endsAt}}），其后{{period}} {{listPrice}}',
    promoFirstMonthBadge: '首月优惠：减免 {{amount}}',
    promoLimitedTimeBadge: '期间限定优惠：减免 {{amount}}（直至 {{endsAt}}）',
    deferredTitle: '设定订阅（高级会员试用期内先不扣款）',
    deferredNoChargeToday: '今日无需付款',
    deferredFirstChargeOn: '首次扣款将于 {{date}} 进行',
    deferredCoversPeriod: '首个付费期：{{start}} 至 {{end}}',
    deferredInvitationTrialNote: '邀请码试用至 {{date}}，系统会于试用结束当日开始首次扣款。',
};
const LOCALE_EN = {
    payTodayTitle: 'Become a premium member now to unlock more features',
    payTodayAmount: 'Only {{amount}} {{period}}',
    promoFirstMonthPrice: 'First month {{dueAmount}}, then {{listPrice}} {{period}}',
    promoLimitedTimePrice: '{{dueAmount}} {{period}} during the offer (until {{endsAt}}), then {{listPrice}} {{period}}',
    promoFirstMonthBadge: 'First-month offer: {{amount}} off',
    promoLimitedTimeBadge: 'Limited-time offer: {{amount}} off (until {{endsAt}})',
    deferredTitle: 'Set up subscription (no charge during premium trial)',
    deferredNoChargeToday: 'No payment due today',
    deferredFirstChargeOn: 'First charge on {{date}}',
    deferredCoversPeriod: 'First paid period: {{start}} to {{end}}',
    deferredInvitationTrialNote: 'Invitation trial until {{date}}. The first charge will start on the trial end date.',
};
const LOCALE_ID = {
    payTodayTitle: 'Jadi anggota premium sekarang untuk membuka lebih banyak fitur',
    payTodayAmount: 'Hanya {{amount}} {{period}}',
    promoFirstMonthPrice: 'Bulan pertama {{dueAmount}}, lalu {{listPrice}} {{period}}',
    promoLimitedTimePrice: '{{dueAmount}} {{period}} selama promo (hingga {{endsAt}}), lalu {{listPrice}} {{period}}',
    promoFirstMonthBadge: 'Promo bulan pertama: potongan {{amount}}',
    promoLimitedTimeBadge: 'Promo terbatas waktu: potongan {{amount}} (hingga {{endsAt}})',
    deferredTitle: 'Atur langganan (belum ditagih selama masa percobaan premium)',
    deferredNoChargeToday: 'Tidak ada pembayaran hari ini',
    deferredFirstChargeOn: 'Tagihan pertama pada {{date}}',
    deferredCoversPeriod: 'Periode berbayar pertama: {{start}} hingga {{end}}',
    deferredInvitationTrialNote: 'Masa percobaan kode undangan hingga {{date}}; tagihan pertama akan dimulai pada tanggal akhir masa percobaan.',
};
const LOCALE_FIL = {
    payTodayTitle: 'Maging premium member ngayon para ma-unlock ang higit pang features',
    payTodayAmount: '{{amount}} lang {{period}}',
    promoFirstMonthPrice: 'Unang buwan {{dueAmount}}, pagkatapos {{listPrice}} {{period}}',
    promoLimitedTimePrice: '{{dueAmount}} {{period}} sa promo (hanggang {{endsAt}}), pagkatapos {{listPrice}} {{period}}',
    promoFirstMonthBadge: 'Unang buwan na promo: bawas {{amount}}',
    promoLimitedTimeBadge: 'Limitadong panahon na promo: bawas {{amount}} (hanggang {{endsAt}})',
    deferredTitle: 'I-set up ang subscription (walang singil habang premium trial)',
    deferredNoChargeToday: 'Walang babayaran ngayon',
    deferredFirstChargeOn: 'Unang singil sa {{date}}',
    deferredCoversPeriod: 'Unang bayad na panahon: {{start}} hanggang {{end}}',
    deferredInvitationTrialNote: 'Invitation trial hanggang {{date}}. Magsisimula ang unang singil sa petsa ng pagtatapos ng trial.',
};
export const DEFAULT_SUBSCRIPTION_UPGRADE_COPY = {
    defaultLocale: LANGUAGE.EN,
    locales: {
        [LANGUAGE.ZH_HANT]: LOCALE_ZH_HANT,
        [LANGUAGE.ZH_HANS]: LOCALE_ZH_HANS,
        [LANGUAGE.EN]: LOCALE_EN,
        [LANGUAGE.ID]: LOCALE_ID,
        [LANGUAGE.FIL]: LOCALE_FIL,
    },
};
export const SUBSCRIPTION_UPGRADE_COPY_LOCALE_KEYS = [
    'payTodayTitle',
    'payTodayAmount',
    'promoFirstMonthPrice',
    'promoLimitedTimePrice',
    'promoFirstMonthBadge',
    'promoLimitedTimeBadge',
    'deferredTitle',
    'deferredNoChargeToday',
    'deferredFirstChargeOn',
    'deferredCoversPeriod',
    'deferredInvitationTrialNote',
];
const getDefaultLocaleRow = (localeCode) => {
    const trimmed = localeCode.trim();
    return (DEFAULT_SUBSCRIPTION_UPGRADE_COPY.locales[trimmed] ??
        DEFAULT_SUBSCRIPTION_UPGRADE_COPY.locales[LANGUAGE.EN]);
};
const readTrimmedString = (raw) => typeof raw === 'string' ? raw.trim() : '';
export const mergeSubscriptionUpgradeCopyLocale = (partial, fallback) => {
    const row = partial ?? {};
    const pick = (key) => {
        const value = readTrimmedString(row[key]);
        return value !== '' ? value : fallback[key];
    };
    return {
        payTodayTitle: pick('payTodayTitle'),
        payTodayAmount: pick('payTodayAmount'),
        promoFirstMonthPrice: pick('promoFirstMonthPrice'),
        promoLimitedTimePrice: pick('promoLimitedTimePrice'),
        promoFirstMonthBadge: pick('promoFirstMonthBadge'),
        promoLimitedTimeBadge: pick('promoLimitedTimeBadge'),
        deferredTitle: pick('deferredTitle'),
        deferredNoChargeToday: pick('deferredNoChargeToday'),
        deferredFirstChargeOn: pick('deferredFirstChargeOn'),
        deferredCoversPeriod: pick('deferredCoversPeriod'),
        deferredInvitationTrialNote: pick('deferredInvitationTrialNote'),
    };
};
const isValidLocaleRow = (row) => Boolean(row?.payTodayTitle?.trim() && row?.payTodayAmount?.trim());
export const applySubscriptionUpgradeTemplate = (template, vars) => Object.entries(vars).reduce((text, [key, value]) => text.split(`{{${key}}}`).join(value), template);
export const applySubscriptionUpgradeAmountTemplate = (template, amountText, periodText) => applySubscriptionUpgradeTemplate(template, {
    amount: amountText,
    ...(periodText ? { period: periodText } : {}),
});
/**
 * Legacy Admin copy hard-coded "每月" / "per month". When billing interval is year
 * and template has no {{period}}, rewrite those phrases to {{period}} so runtime
 * can fill 每年 / per year.
 */
export const ensureUpgradeCopyPeriodPlaceholder = (template, billingInterval) => {
    if (template.includes(SUBSCRIPTION_UPGRADE_COPY_PERIOD_PLACEHOLDER)) {
        return template;
    }
    if (billingInterval !== SUBSCRIPTION_BILLING_INTERVAL.YEAR) {
        return template;
    }
    return template
        .replaceAll('每月', '{{period}}')
        .replaceAll('per month', '{{period}}')
        .replaceAll('Per month', '{{period}}')
        .replaceAll('per bulan', '{{period}}')
        .replaceAll('kada buwan', '{{period}}')
        .replaceAll('首次月費', '首次扣款')
        .replaceAll('首次月费', '首次扣款')
        .replaceAll('First monthly charge', 'First charge')
        .replaceAll('Tagihan bulanan pertama', 'Tagihan pertama')
        .replaceAll('Unang buwanang singil', 'Unang singil');
};
export const resolveSubscriptionUpgradeCopy = (setting, preferredLocale) => {
    const locales = setting?.locales ?? DEFAULT_SUBSCRIPTION_UPGRADE_COPY.locales;
    const defaultLocale = setting?.defaultLocale?.trim() || DEFAULT_SUBSCRIPTION_UPGRADE_COPY.defaultLocale;
    const preferred = preferredLocale.trim();
    const candidates = [preferred, defaultLocale, LANGUAGE.EN, ...Object.keys(locales)];
    for (const key of candidates) {
        if (!key) {
            continue;
        }
        const row = locales[key];
        if (isValidLocaleRow(row)) {
            return mergeSubscriptionUpgradeCopyLocale(row, getDefaultLocaleRow(key));
        }
    }
    return DEFAULT_SUBSCRIPTION_UPGRADE_COPY.locales[LANGUAGE.EN];
};
export const normalizeSubscriptionUpgradeCopySetting = (input) => {
    const locales = {};
    for (const [rawKey, rawValue] of Object.entries(input.locales ?? {})) {
        const key = rawKey.trim();
        if (!key || !rawValue) {
            continue;
        }
        const fallback = getDefaultLocaleRow(key);
        locales[key] = mergeSubscriptionUpgradeCopyLocale(rawValue, fallback);
    }
    const defaultLocale = input.defaultLocale.trim();
    return { defaultLocale, locales };
};
export const parseSubscriptionUpgradeCopySetting = (raw) => {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
        return DEFAULT_SUBSCRIPTION_UPGRADE_COPY;
    }
    const row = raw;
    const defaultLocale = typeof row.defaultLocale === 'string' && row.defaultLocale.trim()
        ? row.defaultLocale.trim()
        : DEFAULT_SUBSCRIPTION_UPGRADE_COPY.defaultLocale;
    const localesRaw = row.locales;
    if (!localesRaw || typeof localesRaw !== 'object' || Array.isArray(localesRaw)) {
        return DEFAULT_SUBSCRIPTION_UPGRADE_COPY;
    }
    return normalizeSubscriptionUpgradeCopySetting({
        defaultLocale,
        locales: localesRaw,
    });
};
//# sourceMappingURL=subscriptionUpgradeCopy.js.map